/****************
 * TRADER VS TRADER. 2026-09-26.
 *
 * Guillaume: "build the Trader vs Trader Comparaison like on Mexc". MEXC keeps
 * the traders being compared in the browser (up to 20), opens a table with one
 * column per trader, and can "Highlight Best Performances" per row.
 *
 * Two things done differently on purpose:
 *
 *   1. Best knows which way is better. MEXC highlights the biggest number on
 *      every row, so on its own profit-share row the most expensive trader is
 *      "best", and every tie lights up. Here each row says whether higher or
 *      lower is better, rows where "best" means nothing (how many trades, how
 *      big the wallet) are never highlighted, and a row where everyone is equal
 *      highlights nobody.
 *   2. The list is kept by the trader's public name (the same slug as their
 *      profile URL), never their exchange id, so a shared link exposes nothing
 *      the profile page does not.
 ****************/

import { useEffect, useMemo, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fromTraderSlug } from '@/lib/traderSlug';

// Five, not MEXC's twenty (Guillaume, 2026-09-26: "only allow like 5 not 20").
export const COMPARE_MAX = 5;
const KEY = 'copyfi.compare.traders';
const EVENT = 'copyfi:compare-changed';

export function readCompareList(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    const v = raw ? JSON.parse(raw) : [];
    return Array.isArray(v) ? v.filter((x) => typeof x === 'string').slice(0, COMPARE_MAX) : [];
  } catch {
    return [];
  }
}

export function writeCompareList(slugs: string[]): void {
  const clean = [...new Set(slugs.filter(Boolean))].slice(0, COMPARE_MAX);
  try {
    localStorage.setItem(KEY, JSON.stringify(clean));
  } catch {
    /* storage refused (private mode): the page still works from its URL */
  }
  try {
    window.dispatchEvent(new Event(EVENT));
  } catch {
    /* no window (tests) */
  }
}

/** Adds one trader. Returns false when the list is already full. */
export function addToCompare(slug: string): boolean {
  const list = readCompareList();
  if (list.includes(slug)) return true;
  if (list.length >= COMPARE_MAX) return false;
  writeCompareList([...list, slug]);
  return true;
}

export function removeFromCompare(slug: string): void {
  writeCompareList(readCompareList().filter((s) => s !== slug));
}

export function subscribeCompare(cb: () => void): () => void {
  const onStorage = (e: StorageEvent) => { if (e.key === KEY) cb(); };
  window.addEventListener(EVENT, cb);
  window.addEventListener('storage', onStorage);
  return () => {
    window.removeEventListener(EVENT, cb);
    window.removeEventListener('storage', onStorage);
  };
}

/** The names being compared, live: any screen that adds or removes re-renders every reader. */
export function useCompareNames(): string[] {
  // No server rendering in this app: both snapshots read the same list.
  const snap = () => JSON.stringify(readCompareList());
  const raw = useSyncExternalStore(subscribeCompare, snap, snap);
  return useMemo(() => JSON.parse(raw) as string[], [raw]);
}

/* ── Which cells are "best" ─────────────────────────────────────────────── */

export type Better = 'high' | 'low' | 'closest_to_zero' | null;

/**
 * For each metric key of lib/traderColumns.tsx: which way is better for someone
 * choosing whom to copy. null = no "best" (a count, a size, a style).
 *
 * Avg Loss is stored NEGATIVE (the average losing trade, -$50), so "lower" would
 * crown the biggest loser: it is judged by its size, closest to zero.
 */
export const BETTER: Record<string, Better> = {
  totalPnl: 'high',
  roi: 'high',
  winRate: 'high',
  profitFactor: 'high',
  sharpe: 'high',
  sortino: 'high',
  avgWin: 'high',
  avgLoss: 'closest_to_zero',
  maxDrawdown: 'low',
  avgDrawdown: 'low',
  totalFees: 'low',
  unrealizedPnl: 'high',
  // Deliberately without a "best": trades, wins, losses, volume, wallet size,
  // margin, leverage, duration, long/short, open positions, biggest and net
  // position. More of them is not better, it is different.
};

/**
 * Indexes of the best value(s) in a row. Nulls and non-numbers are ignored;
 * fewer than two numbers, or everyone equal, returns [] so nothing lights up.
 */
export function bestIndexes(values: (number | null | undefined)[], better: Better): number[] {
  if (!better) return [];
  const score = (v: number) => (better === 'low' ? -v : better === 'closest_to_zero' ? -Math.abs(v) : v);
  const nums = values
    .map((v, i) => ({ v, i }))
    .filter((x): x is { v: number; i: number } => typeof x.v === 'number' && Number.isFinite(x.v));
  if (nums.length < 2) return [];
  const scores = nums.map((x) => score(x.v));
  const top = Math.max(...scores);
  if (scores.every((s) => s === top)) return [];
  return nums.filter((_, k) => scores[k] === top).map((x) => x.i);
}

/** The rows of the comparison, grouped as they are read. `title` is a key in the `traders` namespace. */
export const COMPARE_GROUPS: { title: string; keys: string[] }[] = [
  { title: 'compare.group.results', keys: ['totalPnl', 'roi', 'winRate', 'profitFactor', 'avgWin', 'avgLoss', 'trades', 'winCount', 'lossCount', 'totalFees'] },
  { title: 'compare.group.risk', keys: ['maxDrawdown', 'avgDrawdown', 'worstTrade', 'bestMfe', 'avgMfe', 'pointOfNoReturn', 'bestStopLoss', 'mfeGivenBack', 'profitKept', 'greenToRed', 'bestTakeProfit', 'equityDrawdown', 'cvar', 'calmar', 'sharpe', 'sortino', 'avgLeverage', 'avgMargin'] },
  { title: 'compare.group.style', keys: ['longShort', 'avgDuration', 'profitableMonths', 'totalVolume', 'walletSize'] },
  { title: 'compare.group.rightNow', keys: ['activePositions', 'unrealizedPnl', 'biggestPosition', 'netPosition'] },
];

/**
 * A shared link is the page it was shared from plus ?vs=<slug>,<slug>. The page
 * hosting the pop-up calls this once: it loads those traders into the
 * comparison and says whether to open it.
 */
export function useVsLink(open: () => void) {
  const [params, setParams] = useSearchParams();
  useEffect(() => {
    const vs = params.get('vs');
    if (!vs) return;
    const names = vs.split(',').map((x) => fromTraderSlug(x)).filter(Boolean);
    if (names.length) {
      writeCompareList(names);
      open();
    }
    const p = new URLSearchParams(params);
    p.delete('vs');
    setParams(p, { replace: true });
    // once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
